/**
 * The little Markdown the assistant writes (agents/runtime.py, Style): paragraphs,
 * headings, bullet and numbered lists, tables, code blocks, **bold**, `code` and links.
 * Built as React elements, never as HTML, so nothing it writes can run.
 */

import * as React from 'react'
import { Link } from 'react-router-dom'

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g

function inline(text: string): React.ReactNode[] {
  return text.split(INLINE).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={index} className="rounded bg-surface px-1 font-mono text-[12px]">
          {part.slice(1, -1)}
        </code>
      )
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part)
    if (link) {
      const [, label, href] = link
      if (href.startsWith('/')) {
        return (
          <Link key={index} to={href} className="text-accent underline">
            {label}
          </Link>
        )
      }
      if (/^https?:\/\//.test(href)) {
        return (
          <a key={index} href={href} target="_blank" rel="noreferrer noopener" className="text-accent underline">
            {label}
          </a>
        )
      }
      return label
    }
    return part
  })
}

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim())

export function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const blocks: React.ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const key = blocks.length
    if (line.startsWith('```')) {
      const code: string[] = []
      for (i++; i < lines.length && !lines[i].startsWith('```'); i++) code.push(lines[i])
      i++
      blocks.push(
        <pre key={key} className="overflow-x-auto rounded-md bg-surface p-2 font-mono text-[12px]">
          {code.join('\n')}
        </pre>,
      )
    } else if (/^\s*\|/.test(line) && /^\s*\|?[\s:-]+\|/.test(lines[i + 1] ?? '')) {
      const head = cells(line)
      const rows: string[][] = []
      for (i += 2; i < lines.length && /^\s*\|/.test(lines[i]); i++) rows.push(cells(lines[i]))
      blocks.push(
        <div key={key} className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border">
                {head.map((cell, c) => (
                  <th key={c} className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-b border-border-soft last:border-0">
                  {row.map((cell, c) => (
                    <td key={c} className="px-2 py-1 align-top">
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
    } else if (/^\s*([-*]|\d+\.)\s/.test(line)) {
      const ordered = /^\s*\d+\./.test(line)
      const items: string[] = []
      for (; i < lines.length && /^\s*([-*]|\d+\.)\s/.test(lines[i]); i++) {
        items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s/, ''))
      }
      const List = ordered ? 'ol' : 'ul'
      blocks.push(
        <List key={key} className={ordered ? 'list-decimal space-y-0.5 pl-5' : 'list-disc space-y-0.5 pl-5'}>
          {items.map((item, n) => (
            <li key={n}>{inline(item)}</li>
          ))}
        </List>,
      )
    } else if (/^#{1,6}\s/.test(line)) {
      blocks.push(
        <p key={key} className="font-semibold">
          {inline(line.replace(/^#+\s/, ''))}
        </p>,
      )
      i++
    } else if (line.trim()) {
      const para: string[] = []
      for (; i < lines.length && lines[i].trim() && !/^(```|\s*\||\s*([-*]|\d+\.)\s|#{1,6}\s)/.test(lines[i]); i++) {
        para.push(lines[i])
      }
      if (para.length === 0) para.push(lines[i++])
      blocks.push(
        <p key={key} className="whitespace-pre-wrap">
          {inline(para.join('\n'))}
        </p>,
      )
    } else {
      i++
    }
  }
  return <div className="space-y-2">{blocks}</div>
}
